import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { twilioApi } from '@src/store/rtk/twilio';

export type IdType = number | string;

export type NotificationType = {
  id: IdType;
  createdAt: string;
  isRead: boolean;
  memberImage: string | null;
  memberName: string | null;
  orderingValue: any;
  rideDate: any;
  rideId: IdType | null;
  rideSupportUrl: string | null;
  rideType: string | null;
  statusId: number | null;
  statusName: string | null;
  userId: any;
  vehiclesImage: string | null;
};

type NotificationState = {
  notifications: {
    content: NotificationType[];
    totalElements: number;
    unreadCount: number;
    isLast: boolean;
  };
  userNotification: NotificationType | null;
  dismissedUserNotificationIds: IdType[];
  pageNumber: number;
  pageSize: number;
  basePageSize: number;
  isActiveFilter: boolean;
  hasFetchedSsoNotification: boolean;
};

const initialState: NotificationState = {
  notifications: {
    content: [],
    totalElements: 0,
    unreadCount: 0,
    isLast: false,
  },
  userNotification: null,
  dismissedUserNotificationIds: [],
  pageNumber: 0,
  pageSize: 5,
  basePageSize: 5,
  isActiveFilter: false,
  hasFetchedSsoNotification: false,
};

function mergeNotifications(
  existing: NotificationType[],
  incoming: NotificationType[]
): NotificationType[] {
  const map = new Map(existing.map((n) => [n.id, n]));
  incoming.forEach((n) => map.set(n.id, n));
  return Array.from(map.values());
}

function computeUnreadCount(
  content: NotificationType[],
  userNotification: NotificationType | null
): number {
  const base = content.filter((n) => !n.isRead).length;
  const extra = userNotification && !userNotification.isRead ? 1 : 0;
  return base + extra;
}

function sortByCreatedAtDesc(content: NotificationType[]): NotificationType[] {
  return [...content].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    handleReading: (state, action: PayloadAction<{ id: IdType }>) => {
      const item = state.notifications.content.find(
        (n) => n.id === action.payload.id
      );

      if (item && !item.isRead) {
        item.isRead = true;
      } else if (
        state.userNotification &&
        state.userNotification.id === action.payload.id &&
        !state.userNotification.isRead
      ) {
        state.userNotification.isRead = true;
      } else {
        return;
      }

      state.notifications.unreadCount = computeUnreadCount(
        state.notifications.content,
        state.userNotification
      );
    },

    resetNotifications: (state) => {
      state.notifications.content = [];
      state.notifications.totalElements = 0;
      state.notifications.unreadCount = computeUnreadCount(
        [],
        state.userNotification
      );
      state.notifications.isLast = false;
      state.pageNumber = 0;
      state.pageSize = state.basePageSize;
      state.hasFetchedSsoNotification = false;
      state.userNotification = null;
    },

    incrementPageNumber: (state) => {
      state.pageNumber += 1;
    },

    setPageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = action.payload;
    },

    setBasePageSize: (state, action: PayloadAction<number>) => {
      state.basePageSize = action.payload;
      state.pageNumber = 0;
      state.pageSize = action.payload;
    },

    setIsActiveFilter: (state, action: PayloadAction<boolean>) => {
      state.isActiveFilter = action.payload;
      state.pageNumber = 0;
      state.pageSize = state.basePageSize;
      state.notifications.content = [];
      state.notifications.totalElements = 0;
      state.notifications.isLast = false;
    },

    setUserNotification: (state, action: PayloadAction<NotificationType>) => {
      if (state.dismissedUserNotificationIds.includes(action.payload.id)) {
        return;
      }
      state.userNotification = action.payload;
      state.notifications.unreadCount = computeUnreadCount(
        state.notifications.content,
        state.userNotification
      );
    },

    deleteUserNotification: (state) => {
      if (state.userNotification) {
        state.dismissedUserNotificationIds.push(state.userNotification.id);
      }
      state.userNotification = null;
      state.notifications.unreadCount = computeUnreadCount(
        state.notifications.content,
        null
      );
    },

    markSsoNotificationFetched: (state) => {
      state.hasFetchedSsoNotification = true;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      twilioApi.endpoints.getNotification.matchFulfilled,
      (state, { payload }) => {
        if (!Array.isArray(payload?.content)) return;

        const merged = mergeNotifications(
          state.notifications.content,
          payload.content
        );

        const filtered = merged.filter(
          (n) => !state.dismissedUserNotificationIds.includes(n.id)
        );

        state.notifications.content = sortByCreatedAtDesc(filtered);
        state.notifications.isLast = payload.last;
        state.notifications.totalElements = payload.totalElements || 0;
        state.notifications.unreadCount = computeUnreadCount(
          state.notifications.content,
          state.userNotification
        );
      }
    );
  },
});

export const selectNotification = (state: {
  notifications: NotificationState;
}) => state.notifications;

export const selectDisplayNotifications = (state: {
  notifications: NotificationState;
}): NotificationType[] => {
  const { notifications, userNotification, isActiveFilter } =
    state.notifications;
  const shouldIncludeUserNotification =
    userNotification && (!isActiveFilter || !userNotification.isRead);

  const combined: NotificationType[] = shouldIncludeUserNotification
    ? [userNotification, ...notifications.content]
    : notifications.content;

  return sortByCreatedAtDesc(combined);
};

export const selectEffectiveTotalElements = (state: {
  notifications: NotificationState;
}): number => {
  const { notifications, userNotification, isActiveFilter } =
    state.notifications;

  const userNotificationCounts =
    userNotification && (!isActiveFilter || !userNotification.isRead);

  return notifications.totalElements + (userNotificationCounts ? 1 : 0);
};

export const {
  handleReading,
  resetNotifications,
  incrementPageNumber,
  setPageNumber,
  setBasePageSize,
  setIsActiveFilter,
  setUserNotification,
  deleteUserNotification,
  markSsoNotificationFetched,
} = notificationSlice.actions;

export const notificationReducer = notificationSlice.reducer;
